import React, { useState, useEffect } from "react";
import { Card, Row, Col, Spin, Alert, Radio, Select } from "antd";

import MonthlyStaffAnalytics from "./MonthlyStaffAnalytics";
import { instance } from "../../API/API";
import TotalHoursCard from "./components/TotalHoursCard";
import TotalReportsCard from "./components/TotalReportsCard";
import TotalObjectsCard from "./components/TotalObjectsCard";
import AvgTimePerObjectCard from "./components/AvgTimePerObjectCard";
import AvgDowntimeCard from "./components/AvgDowntimeCard";
import AvgObjectsPerDayCard from "./components/AvgObjectsPerDayCard";
import TotalPlansCard from "./components/TotalPlansCard";
import MonthlyReportsChart from "./components/MonthlyReportsChart";
import MonthlyHoursChart from "./components/MonthlyHoursChart";
import StaffMonthlyHoursTable from "./components/StaffMonthlyHoursTable";

const StaffAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("work"); // 'work', 'downtime', 'overtime'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  useEffect(() => {
    fetchAnalyticsData();
  }, []);
  // Генерация списка годов (от текущего до 2020)
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2020; year--) {
      years.push({ value: year, label: year });
    }
    return years;
  };
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const { data } = await instance.get("analytics/staff_analytics.php");
      console.log("Received data:", data);
      setAnalyticsData(data);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  };

  const calculateAggregatedData = () => {
    if (!analyticsData || analyticsData.length === 0) {
      return {
        gardeners: { current: 0, previous: 0 },
        totalHours: { current: 0, previous: 0 },
        totalReports: { current: 0, previous: 0 },
        totalObjects: { current: 0, previous: 0 },
        avgTimeOnObject: { current: 0, previous: 0 },
        avgDowntime: { current: 0, previous: 0 },
      };
    }

    let totalCurrentHours = 0;
    let totalPreviousHours = 0;
    let totalCurrentReports = 0;
    let totalPreviousReports = 0;
    let totalCurrentObjects = 0;
    let totalPreviousObjects = 0;
    let totalCurrentTimeOnObject = 0;
    let totalPreviousTimeOnObject = 0;

    let avgDowntimeCurrent = 0;
    let avgDowntimePrevious = 0;

    analyticsData.forEach((staff, index) => {
      if (staff.current_year) {
        totalCurrentHours += staff.current_year.total_hours || 0;
        totalCurrentReports += staff.current_year.total_reports || 0;
        totalCurrentObjects += staff.current_year.unique_objects || 0;
        totalCurrentTimeOnObject += staff.current_year.avg_time_per_report || 0;

        if (index === 0) {
          avgDowntimeCurrent = staff.current_year.avg_downtime || 0;
          console.log("Current year downtime:", avgDowntimeCurrent);
        }
      }
      if (staff.previous_year) {
        totalPreviousHours += staff.previous_year.total_hours || 0;
        totalPreviousReports += staff.previous_year.total_reports || 0;
        totalPreviousObjects += staff.previous_year.unique_objects || 0;
        totalPreviousTimeOnObject +=
          staff.previous_year.avg_time_per_report || 0;

        if (index === 0) {
          avgDowntimePrevious = staff.previous_year.avg_downtime || 0;
          console.log("Previous year downtime:", avgDowntimePrevious);
        }
      }
    });

    const avgTimeOnObjectCurrent =
      analyticsData.length > 0
        ? totalCurrentTimeOnObject / analyticsData.length
        : 0;
    const avgTimeOnObjectPrevious =
      analyticsData.length > 0
        ? totalPreviousTimeOnObject / analyticsData.length
        : 0;

    return {
      gardeners: {
        current: analyticsData.length,
        previous: analyticsData.length,
      },
      totalHours: {
        current: totalCurrentHours,
        previous: totalPreviousHours,
      },
      totalReports: {
        current: totalCurrentReports,
        previous: totalPreviousReports,
      },
      totalObjects: {
        current: totalCurrentObjects,
        previous: totalPreviousObjects,
      },
      avgTimeOnObject: {
        current: avgTimeOnObjectCurrent,
        previous: avgTimeOnObjectPrevious,
      },
      avgDowntime: {
        current: Math.max(0, avgDowntimeCurrent),
        previous: Math.max(0, avgDowntimePrevious),
      },
    };
  };

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Spin size="large" />
      </div>
    );

  if (error)
    return <Alert message={error} type="error" style={{ margin: "20px" }} />;

  const aggregatedData = calculateAggregatedData();
  console.log("Aggregated data:", aggregatedData);

  return (
    <div style={{ padding: "16px", minHeight: "100vh" }}>
      <h1
        style={{
          color: "#262626",
          marginBottom: "24px",
          fontSize: "24px",
          fontWeight: "600",
        }}
      >
        Аналитика эффективности садовников
      </h1>

      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} md={8} xl={6}>
          <Card
            style={{
              height: "140px",
              background: "white",
              border: "1px solid #f0f0f0",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
            styles={{
              body: {
                padding: "16px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              },
            }}
          >
            <div
              style={{ color: "#595959", fontSize: "14px", fontWeight: "500" }}
            >
              Количество садовников
            </div>
            <div
              style={{
                color: "#1890ff",
                fontSize: "28px",
                fontWeight: "600",
              }}
            >
              {aggregatedData.gardeners.current}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} xl={6}>
          <TotalHoursCard />
        </Col>
        <Col xs={24} sm={12} md={8} xl={6}>
          <AvgTimePerObjectCard />
        </Col>
        <Col xs={24} sm={12} md={8} xl={6}>
          <AvgDowntimeCard />
        </Col>
        <Col xs={24} sm={12} md={8} xl={6}>
          <TotalObjectsCard />
        </Col>

        <Col xs={24} sm={12} md={8} xl={6}>
          <TotalPlansCard />
        </Col>
        <Col xs={24} sm={12} md={8} xl={6}>
          <TotalReportsCard />
        </Col>
        <Col xs={24} sm={12} md={8} xl={6}>
          <AvgObjectsPerDayCard />{" "}
        </Col>
      </Row>
      <div style={{ width: "100%" }}>
        <MonthlyReportsChart />
      </div>
      <div style={{ width: "100%" }}>
        <MonthlyHoursChart />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          alignItems: "center",
          margin: "20px auto",
        }}
      >
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="work">Отработанное время</Radio.Button>
          <Radio.Button value="downtime">Простои</Radio.Button>
          <Radio.Button value="overtime">Переработки</Radio.Button>
        </Radio.Group>

        <Select
          value={selectedYear}
          onChange={setSelectedYear}
          style={{ width: 120 }}
          options={generateYearOptions()}
        />
      </div>
      <div style={{ width: "100%" }}>
        <StaffMonthlyHoursTable selectedYear={selectedYear} mode={mode} />
      </div>

      {/*      <MonthlyStaffAnalytics/>*/}
    </div>
  );
};

export default StaffAnalytics;
